'use client'

import Cookies from 'js-cookie'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { useCurrentLocale } from 'src/i18n/client/useCurrentLocale'
import { useEffect, useRef, useState } from 'react'
import { cookieIpService } from 'src/_services/client/cookie_ip.service'
import { CookieItemOptions } from './CookieItemOptions'
import { LangChanger } from './LangChanger'
import Switch from './Switch'
import DynamicScriptLoader from 'src/_helpers/client/dynamicScriptLoader'
import Link from 'next/link'
import { useLenis } from 'lenis/react'
import { getPageLink } from 'src/_helpers/getPageLink'
import { CONSENT_GROUPS, getGoogleTag, getOwnGaCookieName } from 'src/_helpers/googleTag'
import SettingIcon from 'public/icons/arrows/setting.svg'
import ChevronIcon from 'public/icons/arrows/chevron.svg'

const getDefaultScript = (cookies, stored) => {
  let script
  if (cookies.lastUpdatedAt === stored?.lastUpdatedAt) {
    const accteptedPreferences = cookies.items.filter(
      (item) => stored.items.find((i) => i.uuid === item.uuid)?.isAccepted,
    )
    script = accteptedPreferences

      .filter((item) => item.script !== null)
      .map((item) => item.script)
      .join('')
  } else {
    script = cookies.items.map((item) => (item.required ? item.script : '')).join('')
  }

  return script
}

const getDefaultPreferences = (cookies, stored) => {
  if (cookies.lastUpdatedAt === stored?.lastUpdatedAt) {
    return {
      ...cookies,
      items: cookies.items.map((item) => ({
        ...item,
        isExpanded: false,
        isAccepted: stored.items.find((i) => i.uuid === item.uuid)?.isAccepted,
      })),
    }
  }

  Cookies.remove('cookie_preferences')

  return {
    ...cookies,
    items: cookies.items.map((item) => ({ ...item, isAccepted: item.required, isExpanded: false })),
  }
}
const isValidJson = (jsonString) => {
  try {
    JSON.parse(jsonString)
  } catch {
    return false
  }
  return true
}

const getStoredPreferences = () => {
  const value = Cookies.get('cookie_preferences')

  return value && isValidJson(value) ? JSON.parse(value) : null
}

const gtmUpdate = () => {
  if (window?.gtag) {
    const jsonCookies = Cookies.get('cookie_preferences') || 'null'
    const cookies = jsonCookies.length > 2 && isValidJson(jsonCookies) ? JSON.parse(jsonCookies) : null
    const acceptedPreferences = Array.isArray(cookies?.items) ? cookies.items : []
    window.gtag(
      'consent',
      'update',
      Object.fromEntries(
        Object.entries(CONSENT_GROUPS).map(([signal, idName]) => [
          signal,
          acceptedPreferences.find((item) => item.idName === idName)?.isAccepted ? 'granted' : 'denied',
        ]),
      ),
    )
  }
}

// Opcja "_ga_*" obejmuje wszystkie istniejace cookies z tym prefiksem (np. _ga_<ID> z GA4).
// Przy znanym identyfikatorze GA4 tylko nasze _ga_<ID>: na domenach typu *.uw.edu.pl widac cookies GA innych serwisow.
const getCookieNames = (title, ownGaCookieName) => {
  if (!title) {
    return []
  }

  const names = Object.keys(Cookies.get())

  if (title === '_ga_*' && ownGaCookieName) {
    return names.filter((name) => name === ownGaCookieName)
  }

  return title.endsWith('*')
    ? names.filter((name) => name.startsWith(title.slice(0, -1)))
    : names.filter((name) => name === title)
}

// Skrypty Google ustawiaja cookies na domenie nadrzednej (.publisphere.pl), bez atrybutu domain sie nie usuna.
// hostOnly: tylko biezacy host - _ga na domenie nadrzednej (np. .uw.edu.pl) jest wspolne z innymi serwisami.
const removeCookie = (name, hostOnly = false) => {
  const hostParts = window.location.hostname.split('.')

  Cookies.remove(name)

  if (hostOnly) {
    Cookies.remove(name, { domain: `.${window.location.hostname}` })

    return
  }

  hostParts.slice(0, -1).forEach((_, index) => {
    Cookies.remove(name, { domain: `.${hostParts.slice(index).join('.')}` })
  })
}

const send = (data, handleSuccess) => {
  gtmUpdate()
  cookieIpService.accept(data, handleSuccess)
}

export default function CookieBot({
  config,
  cookies,
  privacyPolicyPage,
  privacyPolicyBaseUrl = '',
  hostOnlyGaCookies = false,
  externalShowModal,
  setExternalShowModal,
}) {
  const locale = useCurrentLocale()
  const translateEntry = useTranslateEntry()

  const dialogRef = useRef(null)
  const lenis = useLenis()
  const ownGaCookieName = getOwnGaCookieName(getGoogleTag(config?.cookieGtmId))

  const [isShortVersion, setIsShortVersion] = useState(true)

  const handleToggleShortVersion = () => {
    setIsShortVersion((state) => !state)
  }

  const storedPreferences = getStoredPreferences()

  const [script, setScript] = useState(getDefaultScript(cookies, storedPreferences))
  const [preferences, setPreferences] = useState(getDefaultPreferences(cookies, storedPreferences))

  const clearJsonCookies = (cookies) => {
    const cookiesToSave = {}
    Object.keys(cookies).forEach((item) => {
      if (item === 'items') {
        cookiesToSave[item] = cookies[item].map((i) => ({
          idName: i.idName,
          isAccepted: i.isAccepted,
          uuid: i.uuid,
        }))
      } else {
        cookiesToSave[item] = cookies[item]
      }
    })

    return cookiesToSave
  }

  const handleItemAccept = (e) => {
    const uuid = e.target.name

    setPreferences((state) => ({
      ...state,
      items: state.items.map((item) =>
        item.uuid === uuid ? { ...item, isAccepted: !item.isAccepted, idName: item.idName } : item,
      ),
    }))
  }

  const handleItemExpand = (uuid) => {
    setPreferences((state) => ({
      ...state,
      items: state.items.map((item) =>
        item.uuid === uuid ? { ...item, idName: item.idName, isExpanded: !item.isExpanded } : item,
      ),
    }))
  }

  const isAnyItemExpanded = preferences.items.some((item) => item.isExpanded)

  const handleAcceptAll = () => {
    const acceptedPreferences = {
      isAccepted: true,
      items: cookies.items.map((item) => ({ ...item, isAccepted: true, idName: item.idName, isExpanded: false })),
      lastUpdatedAt: cookies.lastUpdatedAt,
    }

    Cookies.set('cookie_preferences', JSON.stringify(clearJsonCookies(acceptedPreferences)), { expires: 182 })

    send(acceptedPreferences, () => {
      setPreferences(acceptedPreferences)

      setScript(
        acceptedPreferences.items
          .filter((item) => item.script !== null)
          .map((item) => item.script)
          .join(''),
      )

      dialogRef.current?.close()
      lenis?.start()
    })
  }

  const handleAcceptSelected = () => {
    const acceptedPreferences = {
      ...preferences,
      items: preferences.items.map((item) => ({ ...item, idName: item.idName, isExpanded: false })),
      isAccepted: true,
    }

    Cookies.set('cookie_preferences', JSON.stringify(clearJsonCookies(acceptedPreferences)), { expires: 182 })
    removeNotAcceptedCookies(acceptedPreferences)

    send(acceptedPreferences, () => {
      setScript(
        acceptedPreferences.items
          .filter((item) => item.isAccepted)
          .filter((item) => item.script !== null)
          .map((item) => item.script)
          .join(''),
      )

      dialogRef.current?.close()
      lenis?.start()
    })
  }

  const handleDecline = () => {
    const acceptedPreferences = {
      isAccepted: true,
      items: cookies.items.map((item) => ({
        ...item,
        isAccepted: item.required,
        idName: item.idName,
        isExpanded: false,
      })),
      lastUpdatedAt: cookies.lastUpdatedAt,
    }

    Cookies.set('cookie_preferences', JSON.stringify(clearJsonCookies(acceptedPreferences)), { expires: 182 })
    removeNotAcceptedCookies(acceptedPreferences)

    send(acceptedPreferences, () => {
      setPreferences(acceptedPreferences)

      setScript(
        acceptedPreferences.items
          .filter((item) => item.isAccepted)
          .filter((item) => item.script !== null)
          .map((item) => item.script)
          .join(''),
      )

      dialogRef.current?.close()
      lenis?.start()
    })
  }

  const removeNotAcceptedCookies = (acceptedPreferences) => {
    acceptedPreferences.items
      .filter((item) => !item.isAccepted)
      .map((item) => item.options)
      .reduce((acc, curr) => [...acc, ...curr], [])
      .forEach((option) => {
        getCookieNames(option.translations[locale]?.title, ownGaCookieName).forEach((name) =>
          removeCookie(name, hostOnlyGaCookies && name === '_ga'),
        )
      })
  }
  useEffect(() => {
    gtmUpdate()
  }, [])

  useEffect(() => {
    if (!externalShowModal) {
      return
    }

    setPreferences(getDefaultPreferences(cookies, storedPreferences, dialogRef))

    dialogRef.current?.showModal()
    lenis?.stop()

    setExternalShowModal(false)
  }, [cookies, externalShowModal, lenis, setExternalShowModal, storedPreferences])

  useEffect(() => {
    if (!dialogRef || cookies.lastUpdatedAt === storedPreferences?.lastUpdatedAt) {
      return
    }

    dialogRef.current?.showModal()
    lenis?.stop()
  }, [dialogRef, lenis, cookies.lastUpdatedAt, storedPreferences?.lastUpdatedAt])

  const privacyPolicyPath = getPageLink(privacyPolicyPage, locale)
  const privacyPolicyLink =
    privacyPolicyBaseUrl && privacyPolicyPath.startsWith('/')
      ? `${privacyPolicyBaseUrl.replace(/\/$/, '')}${privacyPolicyPath}`
      : privacyPolicyPath
  return (
    <>
      {script && <DynamicScriptLoader scripts={script} />}
      <dialog
        ref={dialogRef}
        className={`w-full bg-dark-blue-dark-blue6 cookies px-16 py-24 t:px-60 t:py-40 overflow-x-hidden fixed top-1/2 left-1/2 -translate-1/2 backdrop:bg-basic-black/40 ${isAnyItemExpanded ? 't:max-w-[1194px]' : 't:max-w-[760px]'}`}
        data-lenis-prevent
      >
        <div className={`flex flex-col w-full ${isAnyItemExpanded ? 't:max-w-[1074px] t:mx-auto t:relative' : ''}`}>
          <LangChanger />
          <div className="pb-24 font-sans text-[27px] font-bold leading-[36px] text-dark-blue-dark-blue1">
            {config?.translations[locale]?.cookiesBoxTitle}
          </div>
          <div className="pb-13 font-sans text-body-b2 font-normal leading-175 text-dark-blue-dark-blue1">
            {config?.translations[locale]?.cookiesBoxMsg}
          </div>
          <Link
            className="mb-32 w-fit relative font-serif text-buttons-l font-bold leading-[28px] text-dark-blue-dark-blue7 before:content-[''] before:w-full before:h-1 before:block before:absolute before:bg-current before:bottom-0 before:left-0 before:transition-all hover:before:w-0"
            href={privacyPolicyLink}
          >
            {translateEntry('T_COOKIE_PRIVACY_POLICY')}
          </Link>
          <div className="grid max-t:gap-y-24 t:grid-cols-[1fr_auto_auto] t:items-start t:gap-x-16">
            <button
              type="button"
              className="group flex w-fit items-center gap-8 text-dark-blue-dark-blue7 max-t:mx-auto"
              aria-expanded={!isShortVersion}
              aria-controls="cookies-setting"
              onClick={handleToggleShortVersion}
            >
              <SettingIcon className="size-24 shrink-0" aria-hidden />
              <span className="block w-fit relative font-serif text-buttons-l font-bold leading-[27px] before:content-[''] before:w-full before:h-1 before:block before:absolute before:bg-current before:bottom-0 before:left-0 before:transition-all group-hover:before:w-0">
                {translateEntry('T_COOKIE_SETTING')}
              </span>
              <ChevronIcon
                className={`mt-2 h-6 w-11 shrink-0 transition-transform ${!isShortVersion ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
            <button
              type="button"
              onClick={handleDecline}
              className="w-full cursor-pointer border border-dark-blue-dark-blue2 px-18 pb-13 pt-11 font-serif text-buttons-l font-bold leading-111 text-dark-blue-dark-blue2 transition-colors supports-hover:hover:bg-dark-blue-dark-blue5"
            >
              {translateEntry('T_COOKIE_DECLINE')}*
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="w-full cursor-pointer border border-dark-blue-dark-blue2 px-18 pb-13 pt-11 font-serif text-buttons-l font-bold leading-111 text-dark-blue-dark-blue2 transition-colors supports-hover:hover:bg-dark-blue-dark-blue5"
            >
              {translateEntry('T_COOKIE_ACCEPT')}
            </button>
            <span className="mx-auto relative font-sans text-body-b4 font-normal leading-[27px] text-dark-blue-dark-blue1 t:col-start-2 t:pt-10">
              *{translateEntry('T_COOKIE_INFO')}
            </span>
          </div>
          <div className={`${!isShortVersion ? '' : 'max-h-0 overflow-hidden'}`} id="cookies-setting">
            <div className="pt-32 font-sans text-body-b2 font-normal leading-175 text-dark-blue-dark-blue1">
              {translateEntry('T_COOKIE_INFO_TEXT')}
            </div>
            <div className="pt-32">
              {preferences.items.map((item) => (
                <div
                  key={item.uuid}
                  className="py-12 border-t border-t-dark-blue-dark-blue5 last:border-b last:border-b-dark-blue-dark-blue5"
                >
                  <div className="flex t:items-center t:justify-between max-t:flex-col">
                    <div className="flex items-center gap-x-16">
                      <Switch
                        uuid={item.uuid}
                        value={item.isAccepted}
                        disabled={item.required}
                        handleChange={handleItemAccept}
                        label={item?.translations[locale]?.title}
                      />
                      <span className="block font-sans text-body-b2 font-semibold leading-[27px] text-dark-blue-dark-blue1">
                        {item?.translations[locale]?.title} ({item.options.length})
                      </span>
                    </div>
                    <button
                      type="button"
                      className="group flex items-center gap-10 relative max-t:ml-auto max-t:mt-2xs text-dark-blue-dark-blue7"
                      aria-expanded={item.isExpanded}
                      aria-controls={`cookies-setting-${item.uuid}`}
                      onClick={() => handleItemExpand(item.uuid)}
                    >
                      <span className="block w-fit relative font-serif text-buttons-l font-bold leading-[27px] before:content-[''] before:w-full before:h-1 before:block before:absolute before:bg-current before:bottom-0 before:left-0 before:transition-all group-hover:before:w-0">
                        {item.isExpanded ? translateEntry('T_COOKIE_SHOW_LESS') : translateEntry('T_COOKIE_SHOW_MORE')}
                      </span>
                      <ChevronIcon
                        className={`mt-2 h-6 w-11 shrink-0 transition-transform ${item.isExpanded ? 'rotate-180' : ''}`}
                        aria-hidden
                      />
                    </button>
                  </div>
                  {item.isExpanded && (
                    <div className="w-full" id={`cookies-setting-${item.uuid}`}>
                      <CookieItemOptions options={item.options} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-32 block relative max-t:mx-auto cursor-pointer border border-dark-blue-dark-blue2 px-18 pb-13 pt-11 font-serif text-buttons-l font-bold leading-111 text-dark-blue-dark-blue2 transition-colors supports-hover:hover:bg-dark-blue-dark-blue5"
              onClick={handleAcceptSelected}
            >
              {translateEntry('T_COOKIE_ACCEPT_SELECTED')}
            </button>
          </div>
        </div>
      </dialog>
    </>
  )
}
